import React, { useState, useContext, useEffect } from "react";
import ResponseType from "../components/ResponseType";
import Button from "../components/Button";
import { Question, OptionChoice, Response } from "../types";
import SurveyContext from "../context/surveyContext";
import SideDecorations from "../components/SideDecorations";
import { useNavigate, useParams } from "react-router-dom";
import { scroller } from "react-scroll";
import Spinner from "../components/Spinner";
import Timer from "../components/Timer";
import { useContent, useLanguage } from "../context/languageContext";
import LanguageSelector from "../components/LanguageSelector";
import useApi from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

const SurveyBody: React.FC = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const { contextState, contextDispatch } = useContext(SurveyContext);
  const { language } = useLanguage();
  const content = useContent();
  const { getApiData } = useApi();

  const [optionChoices, setOptionChoices] = useState<OptionChoice[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);

  const navigationPage = parseInt(params.currentPage as string);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(navigationPage);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);


  const { data: questions } = useQuery<Question[]>({
    queryKey: ["questions", language],
    queryFn: () => getApiData(`/survey/questions`),
    initialData: [],
  });


  async function getOptionChoices() {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/survey/questions/option-choices`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Content-Language": language ?? "en",
          Authorization: contextState.user.survey_key ?? "",
        },
      });
      const body = await response.json();
      setOptionChoices(body);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    getOptionChoices();
  }, [language]);

  function scrollTo(id: string) {
    scroller.scrollTo(id, {
      duration: 400,
      delay: 0,
      smooth: true,
      offset: -100,
    });
  }

  // gets an array of unique page numbers from all the questions
  const pages = [...Array.from(new Set(questions.map((question) => question.page_number)))];

  useEffect(() => {
    window.onpopstate = null;
    setCurrentPage(navigationPage);

    // if there is no survey information (proabably due to a refresh), send them to the home page
    if (!contextState.surveyId) {
      navigate("/");
    }
  }, [navigationPage]);

  questions.sort((a, b) => a.question_id - b.question_id)

  const currentQuestions = questions.filter((q) => q.page_number === currentPage);
  const pastQuestions = questions.filter((q) => q.page_number < currentPage);

  async function nextOrSubmit() {
    if (responses.length < pastQuestions.length + currentQuestions.length) {
      setError("Please answer all the questions");
      const firstUnansweredQuestionIndex = questions.findIndex((q) => !responses.find((r) => r.question_id === q.question_id));
      scrollTo(`question-${pastQuestions.length + firstUnansweredQuestionIndex}`);
      setCurrentQuestionIndex(pastQuestions.length + firstUnansweredQuestionIndex);
    }
    // if not the last section, go to the next section
    else if (currentPage < pages.length) {
      navigate(`/survey/${currentPage + 1}`);
      setError("");
      setCurrentPage(currentPage + 1);
      setCurrentQuestionIndex(pastQuestions.length + currentQuestions.length);
      scrollTo("question-0");
    } else {
      setSubmitting(true);
      try {
        // post responses to server
        const response = await fetch(`${process.env.REACT_APP_API_URL}/survey/responses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: contextState.user.survey_key ?? "",
            "Content-Language": language ?? "en",
          },
          body: JSON.stringify({
            responses,
            user: contextState.user,
            startTime: contextState.startTime,
          }),
        });

        // endpoint returns a list of user responses that i need to store in contextState.user.responses
        const userResponses = await response.json();
        contextDispatch({
          type: "setUserResponses",
          payload: userResponses,
        });

        if (response.status === 200) {
          console.log("response", response);
          window.onbeforeunload = null;
          // COME BACK TO THIS
          // navigate("/finished");
          navigate("/results");
        } else {
          //error handling
          console.error(response);
          let body = await response.json();
          console.error(body);
          alert(body.errors[0]);
        }
      } catch (err) {
        console.error(err);
      }
      setSubmitting(false);
    }
  }

  // if the optionChoices haven't been loaded yet, display the loading spinner
  if (questions.length <= 0 || optionChoices.length <= 0) {
    return <Spinner />;
  } else
    return (
      <div className="relative py-20 bg-white overflow-hidden">
        <LanguageSelector />
        <SideDecorations />
        {currentQuestions.map((question: Question, index: number) => (
          <div
            id={`question-${pastQuestions.length + index}`}
            key={index}
            className={`relative px-4 sm:px-6 lg:px-8 ${pastQuestions.length + index === currentQuestionIndex ? "opactiy-100" : "opacity-25"}`}
          >
            <div className="mx-auto mb-24">
              <span className="block text-base text-center text-strata-blue font-semibold tracking-wide uppercase">
                {content("question")} {pastQuestions.length + index + 1} {content("of")} {questions.length}
              </span>
              <div className="flex justify-center">
                <div className="w-full sm:w-2/3 m-2 z-40">
                  <span className="sm:mb-2 mb-5 mt-1 block text-2xl text-center leading-8 font-semibold tracking-normal text-zinc-600 sm:text-4xl">
                    {question.question_title}
                  </span>
                  <ResponseType
                    optionChoices={optionChoices.filter((oc) => oc.option_group_id === question.option_group_id)}
                    question={question}
                    selectedOptionId={responses.find((r) => r.question_id === question.question_id)?.option_choice_id}
                    setSelectedOption={(optionChoiceId: number) => {
                      // update responses
                      const responseIndex = responses.findIndex((response: Response) => response.question_id === question.question_id);
                      const response = {
                        question_id: question.question_id,
                        option_choice_id: optionChoiceId,
                      };
                      const newResponses = [...responses];
                      if (responseIndex === -1) {
                        newResponses.push(response);
                      } else {
                        newResponses[responseIndex] = response;
                      }
                      setResponses(newResponses);

                      index === currentQuestions.length - 1 ? scrollTo("bottom") : scrollTo(`question-${pastQuestions.length + index + 1}`);
                      setCurrentQuestionIndex(pastQuestions.length + index + 1);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <div id="bottom" className="relative px-4 sm:px-6 lg:px-8 text-center mb-1">
          <div className="text-red-500 mb-5">{error}</div>
          <Button onClick={nextOrSubmit} text={currentPage < pages.length ? content("continue") : content("finish")} size="lg" submitting={submitting} />
        </div>
        <Timer />
      </div>
    );
};

export default SurveyBody;
